import {Table} from "../models/table/table";
import {FilterHelper} from "../../../../utils/filter/filter-helper";
import {Row} from "../models/row/row";
import {GroupRow} from "../models/group-row/group-row";
import {SalesUtils} from "../../../../utils/sales/sales-utils";
import {FilterVO} from "../../../../../common/utils/filter/models/filter-vo";
import {FilterKeys} from "../../../../../common/utils/filter/enums/filter-keys";
import {TaxUtilities} from "../../../../../../../utils/tax-utilities";
import {SalesWrapper} from "../../../../utils/sales/sales-wrapper";
import {SalesDto} from "../../../../models/sales/sales-dto";
import {CompositionTypeNl} from "../../../../../../../models/composition-type/composition-type-nl";
import {CompositionDto} from "../../../../../../../models/composition-dto";
import {EventAnalysisStoreService} from "../../../../store/event-analysis-store.service";
import {LinkedDtoRepository} from "../../../../../common/repositories/linked-dto-repository";
import {VATRateHelper} from "../../../../../../../models/common/vat-rate";

export class CompositionTableHelper {

  filterHelper!: FilterHelper;

  constructor() {

  }

  setFilterHelper(filterHelper: FilterHelper) {
    this.filterHelper = filterHelper;
  }

  public sortTable(table: Table): void {
    this.sortGroupRowAlphabetically(table)
    for (let groupRow of table.groupRows) {
      this.sortRowAlphabetically(groupRow)
    }
  }

  private sortGroupRowAlphabetically(table: Table): void {
    table.groupRows.sort((a, b) => {
        return a.name.localeCompare(b.name)
      }
    )
  }

  private sortRowAlphabetically(groupRow: GroupRow): void {
    groupRow.rows.sort((a, b) => {
        return this.compareRows(a, b)
      }
    )
  }

  private compareRows(a: Row, b: Row): number {
    if (a.hasSpecialTag && b.hasSpecialTag)
      return (b.medianPrice - a.medianPrice)
    if (a.hasSpecialTag)
      return 1
    if (b.hasSpecialTag)
      return -1;
    let byName = a.name.localeCompare(b.name)
    if (byName !== 0)
      return byName
    // #534: in individual mode several rows share one article name, so the name alone is not a total order.
    // Tie-break on the rendered swap combination to keep the row order deterministic (the plain, unmodified
    // variant carries no lines and therefore sorts first).
    return CompositionTableHelper.modificationText(a).localeCompare(CompositionTableHelper.modificationText(b))
  }

  /** The row's modification lines flattened to one comparable string (empty for a plain, unmodified row). */
  private static modificationText(row: Row): string {
    return row.modificationLines.map(line => `${line.connector}${line.text}`).join("\n")
  }

  /**
   * Builds the table skeleton: one `GroupRow` per composition TYPE, one `Row` per composition-row key inside it.
   *
   * @param rowKeyFn      what a row IS — `compositionId` (combined) or `compositionVariantKey` ("Kombinationen
   *                      einzeln"). #534 made this a parameter so both modes share one builder.
   * @param rowMappingKey the `LinkedDtoRepository` mapping the row's median price comes from; it MUST match
   *                      `rowKeyFn` (`"compositionId"` ↔ `"compositionVariantKey"`), otherwise every lookup
   *                      misses and rows render at price 0.
   *
   * Both the live wrapper and the filtered-out cache are walked, so a row that is currently filtered away still
   * has its checkbox to switch back on.
   */
  static createTableRowTemplate(salesWrapper: SalesWrapper,
                                salesWrapperCache: SalesWrapper,
                                keyFn: (baseOrderElementDto: SalesDto) => string,
                                groupRowTranslation: (key: string) => string,
                                linkedDtoRepository: LinkedDtoRepository<any>,
                                rowKeyFn: (baseOrderElementDto: SalesDto) => string = (baseOrderElement) => baseOrderElement.compositionId,
                                rowMappingKey: "compositionId" | "compositionVariantKey" = "compositionId"): Table {
    let table = new Table([]);
    let compositionMapping = linkedDtoRepository.getMapping(rowMappingKey) as { [id: string]: CompositionDto }
    let compositionTypeMapping = linkedDtoRepository.getMapping("compositionTypeId") as {
      [id: string]: CompositionTypeNl
    }

    for (let baseOrderElement of salesWrapper.getBaseOrderElements()) {
      CompositionTableHelper.addRowTemplate(baseOrderElement, table, keyFn, groupRowTranslation, rowKeyFn, compositionMapping, compositionTypeMapping);
    }

    for (let baseOrderElement of salesWrapperCache.getBaseOrderElements()) {
      CompositionTableHelper.addRowTemplate(baseOrderElement, table, keyFn, groupRowTranslation, rowKeyFn, compositionMapping, compositionTypeMapping);
    }

    return table
  }

  /** Ensures the group row and the row for one sold line exist in `table`. */
  private static addRowTemplate(baseOrderElement: SalesDto,
                                table: Table,
                                keyFn: (baseOrderElementDto: SalesDto) => string,
                                groupRowTranslation: (key: string) => string,
                                rowKeyFn: (baseOrderElementDto: SalesDto) => string,
                                compositionMapping: { [id: string]: CompositionDto },
                                compositionTypeMapping: { [id: string]: CompositionTypeNl }): void {
    let key = keyFn(baseOrderElement)
    if (Table.getById(key, table) === undefined) {
      Table.addNewGroupRow(new GroupRow(key, groupRowTranslation(key), [], VATRateHelper.GetValueOrDefault(compositionTypeMapping[key]?.vatRate), 0, 0), table)
    }

    let rowKey = rowKeyFn(baseOrderElement)
    if (GroupRow.getById(rowKey, Table.getById(key, table)) === undefined) {
      // The row NAME stays the plain article name even in individual mode — the swap combination is rendered
      // as its own sub-lines underneath (the mapping's `name` is the newline-joined label, which suits the
      // single-line top-sales ranking but not a table cell with its own connector column).
      GroupRow.addRow(new Row(rowKey, baseOrderElement.compositionName, [], baseOrderElement.hasSpecialTag, compositionMapping[rowKey]?.price ?? 0, baseOrderElement.sourceDetailsCost, 0, 0, baseOrderElement.modificationLines), Table.getById(key, table))
    }
  }

  static getSourceDetailCostsOfGroupRow(groupRow: GroupRow): number {
    return groupRow.rows.reduce((acc, row) => {
      return acc + row.sourceDetailPPU * row.soldUnits
    }, 0);
  }

  static getSourceDetailCostsOfTable(table: Table): number {
    return table.groupRows.reduce((acc, groupRow) => {
      return acc + CompositionTableHelper.getSourceDetailCostsOfGroupRow(groupRow)
    }, 0);
  }

  static getRevenueOfTable(table: Table): number {
    return table.groupRows.reduce((acc, groupRow) => {
      return acc + CompositionTableHelper.getRevenueOfGroupRow(groupRow)
    }, 0);
  }

  static getQuantityOfTable(table: Table): number {
    return table.groupRows.reduce((acc, groupRow) => {
      return acc + CompositionTableHelper.getQuantityOfGroupRow(groupRow)
    }, 0);
  }

  static getNetRevenueOfTable(table: Table): number {
    return table.groupRows.reduce((acc, groupRow) => {
      return acc + groupRow.netRevenue
    }, 0);
  }

  static getProfitOfTable(table: Table): number {
    return CompositionTableHelper.getNetRevenueOfTable(table) - CompositionTableHelper.getSourceDetailCostsOfTable(table);
  }

  // private internal functions -> use property instead

  private static getRevenueOfRow(row: Row): number {
    return row.list.reduce((acc, baseOrderElement) => {
      return acc + SalesUtils.getRevenue(baseOrderElement)
    }, 0);
    ;
  }

  private static getQuantityOfRow(row: Row): number {
    return row.list.reduce((acc, baseOrderElement) => {
      return acc + baseOrderElement.quantity
    }, 0);
  }


  private static getRevenueOfGroupRow(groupRow: GroupRow): number {
    return groupRow.rows.reduce((acc, row) => {
      return acc + row.revenue
    }, 0);
  }

  private static getQuantityOfGroupRow(groupRow: GroupRow): number {
    return groupRow.rows.reduce((acc, row) => {
      return acc + row.soldUnits
    }, 0);
  }

  // end of private internal functions


  /**
   * Toggles one row's filter. `filterKey` is the ACTIVE composition-row key (#534): `CompositionId` in combined
   * mode, `CompositionVariantKey` while "Kombinationen einzeln" is on — a row id means nothing without it, and
   * filtering the wrong key would hide either too much or nothing at all.
   */
  static filterComposition(eventAnalysisStore: EventAnalysisStoreService, filterHelper: FilterHelper, groupRow: GroupRow, row: Row, filterKey: FilterKeys = FilterKeys.CompositionId): void {
    let filter = new FilterVO(filterKey, row.id)
    let state = filterHelper.isFilterActive(filter.key, filter.value)
    if (!state) {
      eventAnalysisStore.addFilter(filter)
    } else {
      eventAnalysisStore.removeFilter(filter)
    }

    let indeterminate = CompositionTableHelper.isCompositionTyperFilterIndeterminate(filterHelper, groupRow, filterKey)
    if (!indeterminate) {
      groupRow.isIndeterminate = false
      groupRow.isChecked = state
    } else {
      groupRow.isChecked = state
      groupRow.isIndeterminate = true
    }
  }

  /*static getSourceCostOfGroupRow(groupRow: GroupRow): number{
    return groupRow.rows.reduce((acc, row) => { return acc + row.sourceDetailPPU * row.soldUnits }, 0);
  }*/

  /** Toggles every row of one composition type at once — see {@link filterComposition} on `filterKey`. */
  static filterCompositionType(eventAnalysisStore: EventAnalysisStoreService, groupRow: GroupRow, filterKey: FilterKeys = FilterKeys.CompositionId): void {
    if (!groupRow.isChecked) {
      for (let row of groupRow.rows) {
        eventAnalysisStore.removeFilterWithoutUpdate(new FilterVO(filterKey, row.id))

        groupRow.isChecked = true
      }
    } else {
      for (let row of groupRow.rows) {
        eventAnalysisStore.addFilterWithoutUpdate(new FilterVO(filterKey, row.id))
        groupRow.isChecked = false
      }
    }
    eventAnalysisStore.update()
  }

  /** `true` when SOME (not all) rows of the group are filtered out — see {@link filterComposition} on `filterKey`. */
  static isCompositionTyperFilterIndeterminate(filterHelper: FilterHelper, groupRow: GroupRow, filterKey: FilterKeys = FilterKeys.CompositionId): boolean {
    let activeFilters = 0
    for (let row of groupRow.rows) {
      if (filterHelper.isFilterActive(filterKey, row.id)) {
        activeFilters++
      }
    }
    return activeFilters > 0 && activeFilters < groupRow.rows.length
  }


  /**
   * Overrides the table with the new data
   * @param salesWrapper the currently unfiltered sales
   * @param table the skeleton built by {@link createTableRowTemplate}
   * @param keyFn the group-row (composition type) key
   * @param rowKeyFn what a row is — MUST be the same accessor the skeleton was built with (#534)
   */
  static processDataForTable(salesWrapper: SalesWrapper, table: Table, keyFn: (key: SalesDto) => string, rowKeyFn: (key: SalesDto) => string = (baseOrderElement) => baseOrderElement.compositionId): void {
    for (let groupRow of table.groupRows) {
      for (let row of groupRow.rows) {
        row.list = [];
      }
    }

    for (let baseOrderElement of salesWrapper.getBaseOrderElements()) {
      let key = keyFn(baseOrderElement);
      let groupRow = Table.getById(key, table);

      let row = GroupRow.getById(rowKeyFn(baseOrderElement), groupRow);
      row.list.push(baseOrderElement);
    }

    for (let groupRow of table.groupRows) {
      for (let row of groupRow.rows) {
        row.soldUnits = CompositionTableHelper.getQuantityOfRow(row);
        row.revenue = CompositionTableHelper.getRevenueOfRow(row);
        row.netRevenue = TaxUtilities.getNetFromGross(row.revenue, groupRow.vat);
      }

      groupRow.soldUnits = CompositionTableHelper.getQuantityOfGroupRow(groupRow);
      groupRow.revenue = CompositionTableHelper.getRevenueOfGroupRow(groupRow);
      groupRow.netRevenue = TaxUtilities.getNetFromGross(groupRow.revenue, groupRow.vat);
      groupRow.sourceDetailCosts = CompositionTableHelper.getSourceDetailCostsOfGroupRow(groupRow);
    }
  }
}
